import type {
  Section,
  WebSection,
  WebSectionId,
  WebSectionLeft,
  WebSectionRight,
} from "./types";

/**
 * Merges a user's customized dashboard layout with an FI specified map
 * of sections and indexes.
 */
export const applyPositionOverrides = <T extends string>(
  list: T[],
  overrides: Partial<Record<T, number>>,
): T[] => {
  const result = [...list];
  (Object.entries(overrides) as [string, number][]).forEach(
    ([name, newIndex]) => {
      const currentIndex = result.indexOf(name as T);
      if (currentIndex > -1) {
        result.splice(currentIndex, 1);
      }
      result.splice(newIndex, 0, name as T);
    },
  );
  return result;
};

/**
 * Merges a user's customized dashboard layout with an FI specified map
 * of sections and indexes, applying ordinals.
 */
export const applyWebSectionPositionOverrides = (
  list: WebSection[],
  overrides: Partial<Record<WebSectionId, number>>,
): WebSection[] => {
  const result = [...list];
  (Object.entries(overrides) as [WebSectionId, number][]).forEach(
    ([name, newIndex]) => {
      const currentIndex = result.findIndex(
        (section) => section.section_id === name,
      );
      if (currentIndex > -1) {
        result.splice(currentIndex, 1);
      }

      result.splice(newIndex, 0, {
        section_id: name,
      });
    },
  );
  return result;
};

/**
 * Updates a user's customized section order (mobile, and left or right web columns)
 * with all sections that should or should not be shown.
 */
export const enforceVisibleSections = <T extends Section>(
  userSectionOrder: T[],
  _allVisibleSections: T[],
): T[] => {
  const visibleSections: T[] = [];
  const remainingSections = [..._allVisibleSections];

  userSectionOrder.forEach((userSection) => {
    if (remainingSections.includes(userSection)) {
      visibleSections.push(userSection);
      remainingSections.splice(remainingSections.indexOf(userSection), 1);
    }
  });

  visibleSections.push(...remainingSections);

  return [...new Set(visibleSections)];
};

export const filterVisibleSections = (
  sections: WebSection[],
  visibleSectionIds: WebSectionId[],
): WebSection[] => {
  const visibleSections = sections.filter((section) =>
    visibleSectionIds.includes(section.section_id),
  );

  const seen = new Set<string>(
    visibleSections.map((section) => section.section_id),
  );

  const missingVisibleSections = visibleSectionIds
    .filter((sectionId) => !seen.has(sectionId))
    .map((section_id) => ({ section_id }));

  return [...visibleSections, ...missingVisibleSections];
};

export const convertToWebSection = (
  webSection: WebSectionLeft | WebSectionRight,
): WebSection => {
  return {
    section_id: webSection,
  };
};
